import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import {capitalize} from 'lodash';
import * as Moment from 'moment';
import {BehaviorSubject, Observable} from 'rxjs';
import {retry, tap} from 'rxjs/operators';
import {
  IAdvancedOrderSearchParams,
  IOrderSearch,
  RequisitionLabelViewModel,
  TransmissionModel
} from '../../../shared/models/ocl';
import {FusionAuthService} from '../../../shared/services/fusion-auth';
import {DateHelperService, UrlProviderService} from '../../../shared/services/general';
import {ObjectUtils, SECURED_STORE} from '../../../shared/utils';
import {AppState} from '../../../stores/app.state';
import {CumulativeResultsCollection, OrderPatientInformation} from '../../features/results/models/cumulative-result';
import {BillTo} from '../models/billing/bill-type';
import {Document} from '../models/common/document';
import {Order, OrderStatus} from '../../features/results/models/order';
import {
  OrderBillingInformation,
  OrderBillingInformationEditable
} from '../../features/results/models/order-billing-information';
import {OrderTestTypes} from '../../features/results/models/order-tests';
import {MedicationResult, MicrobiologyResult, PathologyResult, ResultBase} from '../../features/results/models/results';
import {
  MultipleOrdersSignature,
  MultipleRequisitionsSignature,
  OrderSignature,
  RequisitionSignature
} from "../models/signature";

@Injectable()
export class OrdersService {

  constructor(
    private urlProvider: UrlProviderService,
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private fusionAuthService: FusionAuthService,
    private router: Router,
    private dateHelper: DateHelperService
  ) {
    this.customStore = this.customStore.bind(this);
  }

  public advancedSearchStore(params: IAdvancedOrderSearchParams): CustomStore {
    // @ts-ignore
    // TODO
    params.status = OrderStatus[params.status];
    const dataSource = params.dataSource;
    // @ts-ignore
    delete params.dataSource;

    return SECURED_STORE({
      dataSource,
      loadParams: params,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      routerService: this.router,
      loadUrl: `${this.urlProvider.ApiUrl}/v2/orders/advanced-search`,
    });
  }

  public orderSignature(orderId: number): Observable<OrderSignature> {
    return this.httpClient.get<OrderSignature>(`${this.urlProvider.ApiUrl}/v2/orders/${orderId}/signature`).pipe(retry(2));
  }

  public unsign(id: number): Observable<null> {
    return this.httpClient.post<null>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/unsign`, {});
  }

  public signOrders(signature: MultipleOrdersSignature): Observable<null> {
    signature = ObjectUtils.capitalize(signature) as MultipleOrdersSignature;
    return this.httpClient.post<null>(`${this.urlProvider.ApiUrl}/v2/orders/sign-multiple`, signature);
  }

  public getLabels(id: number): Observable<RequisitionLabelViewModel[]> {
    return this.httpClient.get<RequisitionLabelViewModel[]>(`${this.urlProvider.ApiUrl}/labels/orders/${id}`)
      .pipe(
        retry(2),
        tap(labels => {
          labels.forEach((label, index) => {
            label.id = index;
            return label;
          });
        })
      );
  }

  public customStore(externalFilters: IOrderSearch, alertResults: boolean = false, clientIds?: number[], patientIds?: number[],
                     physicianIds?: number[], dataSource?: BehaviorSubject<DataSource>): CustomStore {
    const loadParams = {
      ...externalFilters,
      startDate: Moment(externalFilters.startDate, 'MM/DD/YYYY').format('MM/DD/YYYY'),
      endDate: Moment(externalFilters.endDate, 'MM/DD/YYYY').format('MM/DD/YYYY'),
      client: externalFilters.client ? externalFilters.client.id : undefined,
      unit: externalFilters.unit ? externalFilters.unit.id : undefined,
      patient: externalFilters.patient ? externalFilters.patient.id : undefined,
      clientIds,
      patientIds,
      physicianIds,
      alertResults,
    };
    // @ts-ignore
    loadParams.status = OrderStatus[loadParams.status];

    return SECURED_STORE({
      dataSource: dataSource as BehaviorSubject<DataSource>,
      loadParams,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      routerService: this.router,
      loadUrl: `${this.urlProvider.ApiUrl}/v2/orders/remote-operations`,
    });
  }

  public acknowledgeOrder(ordersIdsOnQueryString: string): Observable<void> {
    return this.httpClient.put<void>(
      `${this.urlProvider.ApiUrl}/v2/orders/acknowledge?${ordersIdsOnQueryString}username=${this.fusionAuthService.Profile ? this.fusionAuthService.Profile.username : ''}`,
      {},
    );
  }

  public downloadOrderDocuments(orderId: number, documentName: string) {
    const url = `${this.urlProvider.ApiUrl}/v2/orders/${orderId}/documents/download?name=${documentName}`;
    const httpOptions = {
      headers: new HttpHeaders(),
      responseType: 'blob' as 'json',
    };
    httpOptions.headers.append('Authorization', this.fusionAuthService.getToken());
    return this.httpClient.get<any>(url, httpOptions).pipe(retry(2));
  }

  public getOderDocuments(id: number = -1): Observable<Document[]> {
    return this.httpClient.get<Document[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/documents`).pipe(retry(2));
  }

  public uploadDocument(requisitionId: number, fileBase64: string, fileName: string): Observable<any> {
    return this.httpClient.post<any>(
      `${this.urlProvider.ApiUrl}/v2/orders/${requisitionId}/documents`, {
        fileBase64,
        fileName,
      }, {
        reportProgress: true,
      },
    );
  }

  public sendTransmission(transmission: TransmissionModel): Observable<any> {
    return this.httpClient.post<any>(`${this.urlProvider.ApiUrl}/v2/orders/transmission`, transmission);
  }

  public changeBillTo(billTo: string, requisitionsIds: number[]) {
    // @ts-ignore
    let url = `/v2/orders/change-bill-to?billTo=${BillTo[capitalize(billTo)]}&`;
    const ids: string[] = [];
    requisitionsIds.map(x => {
      ids.push(`ids=${x}`);
    });

    if (ids.length > 0) {
      url += ids.join('&');
    }

    return this.httpClient.put(`${this.urlProvider.ApiUrl}${url}`, {reportProgress: true});
  }

  public getOder(id: number): Observable<Order> {
    return this.httpClient.get<Order>(`${this.urlProvider.ApiUrl}/v2/orders/${id}`).pipe(retry(2));
  }

  public removeOrderDocument(orderId: number, documentId: number) {
    const url = `${this.urlProvider.ApiUrl}/v2/orders/${orderId}/documents/${documentId}`;

    return this.httpClient.delete<any>(url);
  }


  public getOrderBillingInfo(id: number): Observable<OrderBillingInformation> {
    return this.httpClient.get<OrderBillingInformation>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/billing-information`).pipe(retry(2));
  }

  public updateMissingInfo(id: number, info: OrderBillingInformationEditable): Observable<OrderBillingInformationEditable> {
    info = ObjectUtils.capitalize(info) as OrderBillingInformationEditable;
    return this.httpClient.put<OrderBillingInformationEditable>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/billing-information`, info);
  }

  public getOrderTestsTypes(id: number): Observable<OrderTestTypes> {
    return this.httpClient
      .get<OrderTestTypes>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/results-types`)
      .pipe(retry(2));
  }

  public getOrderClinicalResults(id: number) {
    return this.httpClient
      .get<ResultBase[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/clinical-results`)
      .pipe(retry(2));
  }

  public getOrderToxicologyAlphaResults(id: number) {
    return this.httpClient
      .get<ResultBase[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/toxicology-alpha-results`)
      .pipe(retry(2));
  }

  public getOrderToxicologyConsistentResults(id: number) {
    return this.httpClient
      .get<ResultBase[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/toxicology-expected-results`)
      .pipe(retry(2));
  }

  public getOrderToxicologyInconsistentNegativeResults(id: number) {
    return this.httpClient
      .get<ResultBase[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/toxicology-unexpected-negative-results`)
      .pipe(retry(2));
  }

  public getOrderToxicologyInconsistentPositiveResults(id: number) {
    return this.httpClient
      .get<ResultBase[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/toxicology-unexpected-positive-results`)
      .pipe(retry(2));
  }

  public getOrderToxicologyOtherMedications(id: number) {
    return this.httpClient
      .get<MedicationResult[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/toxicology-other-medications`)
      .pipe(retry(2));
  }

  public getPathologyResults(id: number) {
    return this.httpClient
      .get<PathologyResult[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/pathology-results`)
      .pipe(retry(2));
  }

  public getMicrobiologyResults(id: number) {
    return this.httpClient
      .get<MicrobiologyResult[]>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/microbiology-results`)
      .pipe(retry(2));
  }

  public getOrderPatientInformation(id: number) {
    return this.httpClient
      .get<OrderPatientInformation>(`${this.urlProvider.ApiUrl}/v2/orders/${id}/cumulative-result-info`)
      .pipe(retry(2));
  }

  public getCumulativeResults(orderId: number, clientsId: number, physicianIds: number[] = [], startDate: Date, endDate: Date, records: number, type: string): Observable<any> {
    let queryString = `startDate=${this.dateHelper.formatDate(startDate)}&endDate=${this.dateHelper.formatDate(endDate)}` +
      `&records=${records}&type=NORMAL&clientId=${clientsId}` + `${physicianIds && physicianIds.length > 0 ? '&' : ''}`;
    if (physicianIds && physicianIds.length > 0) {
      physicianIds.map(x => queryString += physicianIds.indexOf(x) === physicianIds.length - 1 ? `physicianIds=${x}` : `physicianIds=${x}&`);
    }

    return this.httpClient.get<CumulativeResultsCollection[]>(
      `${this.urlProvider.ApiUrl}/v2/orders/${orderId}/${type}-cumulative-results?${queryString}`,
    ).pipe(retry(2));
  }
}
